// Lightweight visitor-event tracker. Fire-and-forget POSTs to /api/panel/track.
// Designed to be silent — failures never break the user flow.
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/panel/track`;
const SESSION_KEY = 'entorid_session_id';

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = (crypto?.randomUUID?.() || `s_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

// Throttle: max 1 event of same type per 1s window
const lastSent = new Map();
function shouldThrottle(type) {
  const now = Date.now();
  const last = lastSent.get(type) || 0;
  if (now - last < 1000) return true;
  lastSent.set(type, now);
  return false;
}

export function trackEvent(type, meta = {}) {
  if (typeof window === 'undefined') return;
  if (shouldThrottle(type)) return;
  const payload = {
    type,
    path: window.location.pathname + window.location.search,
    session_id: getSessionId(),
    meta,
  };
  // Use sendBeacon if available (more reliable on page-unload events)
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
      navigator.sendBeacon(API_URL, blob);
      return;
    }
  } catch { /* fallthrough to fetch */ }
  // Fallback: fire-and-forget fetch
  fetch(API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => { /* silent */ });
}

export const trackPageview = (extra) => trackEvent('pageview', extra);
export const trackCallClick = (extra) => trackEvent('call_click', extra);
export const trackWhatsAppClick = (extra) => trackEvent('whatsapp_click', extra);
export const trackFormSubmit = (extra) => trackEvent('form_submit', extra);
export const trackChatOpen = (extra) => trackEvent('chat_open', extra);
export const trackBookClick = (extra) => trackEvent('book_click', extra);

// =====================================================================
// GA4 / Google Ads eCommerce helpers — push to GTM dataLayer
// =====================================================================
function dlPush(payload) {
  try {
    window.dataLayer = window.dataLayer || [];
    // Clear previous ecommerce object so it doesn't merge between events (GA4 best practice)
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push(payload);
  } catch { /* silent */ }
}

/**
 * Fire on every calculator/contact form submission (Submit Enquiry, WhatsApp lead, free inspection).
 * For residential leads the `value` is the estimated price; for commercial/industrial it's 0
 * (true value comes later at purchase).
 */
export function gtmLeadSubmit({ leadId, segment, pestType, pestLabel, industryField, areaSqft, value, city, source }) {
  dlPush({
    event: 'generate_lead',
    lead_segment: segment || 'residential',
    lead_source: source || 'calculator',
    industry_field: industryField || null,
    currency: 'INR',
    value: Number(value) || 0,
    ecommerce: {
      currency: 'INR',
      value: Number(value) || 0,
      items: [{
        item_id: pestType || 'unknown',
        item_name: pestLabel || pestType || 'Pest Control',
        item_category: segment || 'residential',
        item_category2: industryField || undefined,
        quantity: 1,
        price: Number(value) || 0,
      }],
    },
    lead_id: leadId || null,
    area_sqft: areaSqft || null,
    city: city || null,
  });
}

/**
 * Fire when the user clicks "Pay Now" → just before Razorpay opens.
 * Residential calculator only — commercial/industrial don't checkout online.
 */
export function gtmBeginCheckout({ leadId, segment, pestType, pestLabel, value, finalValue, coupon }) {
  dlPush({
    event: 'begin_checkout',
    currency: 'INR',
    value: Number(finalValue || value) || 0,
    coupon: coupon || null,
    ecommerce: {
      currency: 'INR',
      value: Number(finalValue || value) || 0,
      coupon: coupon || null,
      items: [{
        item_id: pestType || 'unknown',
        item_name: pestLabel || pestType || 'Pest Control',
        item_category: segment || 'residential',
        quantity: 1,
        price: Number(value) || 0,
      }],
    },
    lead_id: leadId || null,
  });
}

/**
 * Fire after a successful Razorpay payment (residential calculator).
 * `transactionId` should be the Razorpay payment_id so Google Ads can dedupe & match revenue.
 */
export function gtmPurchase({ transactionId, leadId, segment, pestType, pestLabel, value, finalValue, coupon, discount }) {
  const v = Number(finalValue || value) || 0;
  dlPush({
    event: 'purchase',
    currency: 'INR',
    value: v,
    coupon: coupon || null,
    transaction_id: transactionId,
    ecommerce: {
      currency: 'INR',
      transaction_id: transactionId,
      value: v,
      coupon: coupon || null,
      discount: Number(discount) || 0,
      items: [{
        item_id: pestType || 'unknown',
        item_name: pestLabel || pestType || 'Pest Control',
        item_category: segment || 'residential',
        quantity: 1,
        price: Number(value) || 0,
        discount: Number(discount) || 0,
      }],
    },
    lead_id: leadId || null,
  });
}
